// Mock API 服务层 - 本地开发使用，无需任何外部依赖
// 所有数据存储在 LocalStorage 中

// ==================== 数据存储工具 ====================
class LocalStorageDB {
  private prefix = 'nexuscube_';

  get<T>(key: string): T | null {
    const data = localStorage.getItem(this.prefix + key);
    return data ? JSON.parse(data) : null;
  }

  set<T>(key: string, value: T): void {
    localStorage.setItem(this.prefix + key, JSON.stringify(value));
  }

  getList<T>(key: string): T[] {
    return this.get<T[]>(key) || [];
  }

  addToList<T extends { id: string }>(key: string, item: T): T {
    const list = this.getList<T>(key);
    list.push(item);
    this.set(key, list);
    return item;
  }

  updateInList<T extends { id: string }>(
    key: string,
    id: string,
    updates: Partial<T>
  ): T | null {
    const list = this.getList<T>(key);
    const index = list.findIndex((item) => item.id === id);
    if (index === -1) return null;

    list[index] = { ...list[index], ...updates };
    this.set(key, list);
    return list[index];
  }

  deleteFromList<T extends { id: string }>(key: string, id: string): boolean {
    const list = this.getList<T>(key);
    const filtered = list.filter((item) => item.id !== id);
    if (filtered.length === list.length) return false;

    this.set(key, filtered);
    return true;
  }

  findInList<T extends { id: string }>(key: string, predicate: (item: T) => boolean): T[] {
    return this.getList<T>(key).filter(predicate);
  }
}

const db = new LocalStorageDB();

// ==================== 工具函数 ====================
const generateId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// ==================== 类型定义 ====================
export interface User {
  id: string;
  email: string;
  name: string;
  nickname?: string;
  avatar_url?: string;
  phone?: string;
  role: 'creator' | 'participant' | 'both';
  field?: string;
  bio?: string;
  plan: 'free' | 'pro' | 'enterprise';
  plan_expires_at?: string;
  api_key?: string;
  created_at: string;
}

export interface IPReport {
  id: string;
  user_id: string;
  name: string;
  field: string;
  tags: string[];
  positioning: string;
  persona: any;
  content_strategy: any[];
  monetization: any[];
  platform_bios: any;
  created_at: string;
}

export interface Event {
  id: string;
  creator_id: string;
  title: string;
  description: string;
  category: string;
  event_type: 'online' | 'offline';
  start_time: string;
  end_time: string;
  location: string;
  max_participants: number;
  current_participants: number;
  price: number;
  cover_image: string;
  status: 'active' | 'completed' | 'cancelled';
  rating: number;
  view_count: number;
  created_at: string;
}

export interface Registration {
  id: string;
  event_id: string;
  user_id: string;
  name: string;
  phone: string;
  wechat?: string;
  email?: string;
  status: 'confirmed' | 'attended' | 'cancelled';
  rating?: number;
  review?: string;
  created_at: string;
}

export interface Material {
  id: string;
  type: 'template' | 'image' | 'video' | 'audio';
  title: string;
  description: string;
  url: string;
  thumbnail_url: string;
  category: string;
  tags: string[];
  usage_count: number;
  favorite_count: number;
  is_featured: boolean;
  created_at: string;
}

export interface ContentGeneration {
  id: string;
  user_id: string;
  type: 'poster' | 'copywriting' | 'script' | 'video';
  config: any;
  result_url?: string;
  result_data?: any;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  created_at: string;
}

// ==================== 初始化演示数据 ====================
const initDemoData = () => {
  // 检查是否已初始化
  if (db.get('initialized')) return;

  // 创建演示用户
  const demoUser: User = {
    id: 'demo-user-1',
    email: 'demo@nexuscube.com',
    name: '张主理',
    avatar_url: 'https://api.dicebear.com/7.x/avataaars/svg?seed=demo',
    role: 'creator',
    plan: 'pro',
    created_at: new Date().toISOString(),
  };
  db.addToList('users', demoUser);
  db.set('currentUser', demoUser);

  // 创建演示活动
  const demoEvents: Event[] = [
    {
      id: 'event-1',
      creator_id: demoUser.id,
      title: '非遗手工艺体验工作坊',
      description: '体验传统扎染技艺，制作专属围巾',
      category: '非遗文化',
      event_type: 'offline',
      start_time: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      end_time: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000 + 3 * 60 * 60 * 1000).toISOString(),
      location: '上海市黄浦区文化中心',
      max_participants: 20,
      current_participants: 12,
      price: 299,
      cover_image: 'https://picsum.photos/800/600?random=1',
      status: 'active',
      rating: 4.8,
      view_count: 156,
      created_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    },
    {
      id: 'event-2',
      creator_id: demoUser.id,
      title: 'IP定位线上分享会',
      description: '如何打造个人IP，从0到1的实战经验分享',
      category: '品牌营销',
      event_type: 'online',
      start_time: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
      end_time: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000 + 2 * 60 * 60 * 1000).toISOString(),
      location: '腾讯会议',
      max_participants: 100,
      current_participants: 67,
      price: 0,
      cover_image: 'https://picsum.photos/800/600?random=2',
      status: 'active',
      rating: 4.6,
      view_count: 289,
      created_at: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    },
  ];
  demoEvents.forEach((event) => db.addToList('events', event));

  // 创建演示素材
  const categories = ['活动海报', '社交媒体', '宣传单页', '名片设计'];
  const demoMaterials: Material[] = Array.from({ length: 12 }, (_, i) => ({
    id: `material-${i + 1}`,
    type: 'template' as const,
    title: `${categories[i % 4]}模板 ${Math.floor(i / 4) + 1}`,
    description: '精美设计模板，可一键使用',
    url: `https://picsum.photos/800/1200?random=${i + 10}`,
    thumbnail_url: `https://picsum.photos/400/600?random=${i + 10}`,
    category: categories[i % 4],
    tags: ['活动', '推广', '设计'],
    usage_count: Math.floor(Math.random() * 500),
    favorite_count: Math.floor(Math.random() * 100),
    is_featured: i < 4,
    created_at: new Date(Date.now() - i * 24 * 60 * 60 * 1000).toISOString(),
  }));
  demoMaterials.forEach((material) => db.addToList('materials', material));

  db.set('initialized', true);
};

// 初始化数据
initDemoData();

// ==================== 认证服务 ====================
export const authService = {
  async signIn(email: string, password: string) {
    await delay(500);

    // 演示模式：任何邮箱都可以登录
    let user = db.findInList<User>('users', (u) => u.email === email)[0];

    if (!user) {
      user = {
        id: generateId(),
        email,
        name: email.split('@')[0],
        role: 'creator',
        plan: 'free',
        created_at: new Date().toISOString(),
      };
      db.addToList('users', user);
    }

    db.set('currentUser', user);
    return { user };
  },

  async signUp(email: string, password: string, userData: Partial<User>) {
    await delay(500);

    const user: User = {
      id: generateId(),
      email,
      name: userData.name || email.split('@')[0],
      role: userData.role || 'creator',
      plan: 'free',
      created_at: new Date().toISOString(),
    };

    db.addToList('users', user);
    db.set('currentUser', user);
    return { user };
  },

  async signOut() {
    db.set('currentUser', null);
  },

  async getCurrentUser() {
    return db.get<User>('currentUser');
  },

  async updateProfile(userId: string, updates: Partial<User>) {
    const user = db.updateInList<User>('users', userId, updates);
    if (user) {
      db.set('currentUser', user);
    }
    return user;
  },

  async changePassword(passwords: any) {
    await delay(500);
    return { message: 'Password updated (Mock)' };
  },

  async rotateApiKey() {
    await delay(500);
    const newKey = `sk_mock_${generateId()}`;
    const user = await this.getCurrentUser();
    if (user) {
      this.updateProfile(user.id, { api_key: newKey });
    }
    return { api_key: newKey };
  },
};

// ==================== IP定位服务 ====================
export const ipAnalysisService = {
  async createReport(reportData: Omit<IPReport, 'id' | 'created_at'>) {
    await delay(1000);

    const report: IPReport = {
      id: generateId(),
      ...reportData,
      created_at: new Date().toISOString(),
    };

    db.addToList('ip_reports', report);
    return report;
  },

  async getReports(userId: string) {
    return db.findInList<IPReport>('ip_reports', (r) => r.user_id === userId);
  },

  async getReportById(reportId: string) {
    return db.findInList<IPReport>('ip_reports', (r) => r.id === reportId)[0];
  },

  async generateIPPositioning(input: any) {
    await delay(3000);

    return {
      positioning: `${input.name}是一位专注于${input.field}领域的专业人士，通过${input.contentTypes?.join('、') || '多种形式'}，为${input.targetAudience}提供专业指导和服务。`,
      persona: {
        identity_tags: ['专业', '热情', '创新'],
        personality: ['亲和力强', '善于沟通', '注重细节'],
        core_values: ['传承', '创新', '分享'],
      },
      content_strategy: [
        { type: '专业知识分享', description: '深入浅出讲解行业知识', percentage: 40 },
        { type: '案例故事', description: '真实案例打造信任感', percentage: 30 },
        { type: '互动答疑', description: '解决粉丝实际问题', percentage: 20 },
        { type: '活动预告', description: '线下活动和课程推广', percentage: 10 },
      ],
      monetization: [
        { method: '线下活动/工作坊', description: '组织体验活动收取费用', priority: 1 },
        { method: '在线课程', description: '录制专业课程售卖', priority: 2 },
        { method: '咨询服务', description: '一对一深度咨询', priority: 3 },
        { method: '产品/工具销售', description: '周边产品或工具包', priority: 4 },
      ],
      platform_bios: {
        douyin: `${input.field}传承人 | ${input.tags?.join('/') || '专业创作者'} | 带你感受传统之美`,
        xiaohongshu: `分享${input.field}的点点滴滴 ✨ | ${input.tags?.[0] || '创作'}爱好者 | 一起探索传统文化`,
        wechat_channels: `${input.name} | ${input.field}推广者 | 每周分享${input.contentTypes?.[0] || '精彩内容'}`,
      },
    };
  },
};

// ==================== 内容生成服务 ====================
export const contentService = {
  async createGeneration(generationData: Omit<ContentGeneration, 'id' | 'status' | 'created_at'>) {
    const generation: ContentGeneration = {
      id: generateId(),
      ...generationData,
      status: 'pending',
      created_at: new Date().toISOString(),
    };

    db.addToList('content_generations', generation);
    return generation;
  },

  async getGenerations(userId: string, type?: string) {
    const list = db.findInList<ContentGeneration>('content_generations', (g) => g.user_id === userId);
    if (type) {
      return list.filter((g) => g.type === type);
    }
    return list;
  },

  async generatePoster(config: any) {
    await delay(2000);
    return {
      url: `https://picsum.photos/1080/1440?random=${Date.now()}`,
      width: 1080,
      height: 1440,
    };
  },

  async generateCopywriting(config: any) {
    await delay(2000);
    return Array.from({ length: config.count || 3 }, (_, i) => ({
      version: i + 1,
      content: `【${config.topic || '精彩活动'}】\n\n${config.keywords?.join('、') || '精彩内容'}等你来体验！\n\n这是为${config.platform || '社交媒体'}精心准备的${config.style || '专业'}文案。\n\n#${config.keywords?.[0] || '活动'} #${config.keywords?.[1] || '推广'}`,
      hashtags: config.keywords || [],
      characterCount: 120 + Math.floor(Math.random() * 80),
    }));
  },

  async generateScript(config: any) {
    await delay(2500);
    return {
      hook: '你知道吗？这个技能可以让你的生活更美好！',
      problem: `很多人在${config.topic || '这个方面'}遇到困难，不知道如何开始...`,
      solution: `今天就来教大家一个超实用的方法，只需要3步就能掌握！`,
      callToAction: '关注我，下期教你更多实用技巧！',
      shootingTips: [
        { scene: '开场', action: '特写镜头', expression: '微笑、眼神坚定' },
        { scene: '讲解', action: '中景', expression: '认真、专注' },
        { scene: '结尾', action: '近景', expression: '热情、邀请' },
      ],
      estimatedDuration: config.duration || '60秒',
    };
  },

  async generateVideo(config: any) {
    await delay(5000);
    return {
      url: 'https://storage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4',
      duration: 45,
      size: '8.5MB',
      status: 'completed',
    };
  },

  async getTodayStats(userId: string) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const list = db.findInList<ContentGeneration>(
      'content_generations',
      (g) => g.user_id === userId && new Date(g.created_at) >= today
    );

    const stats = { poster: 0, copywriting: 0, script: 0, video: 0 };
    list.forEach((item) => {
      if (item.type in stats) {
        stats[item.type as keyof typeof stats]++;
      }
    });

    return stats;
  },
};

// ==================== 活动服务 ====================
export const eventService = {
  async uploadFile(file: File) {
    await delay(1000);
    return { url: 'https://picsum.photos/400/400?random=upload' };
  },

  async createEvent(eventData: Omit<Event, 'id' | 'current_participants' | 'rating' | 'view_count' | 'created_at'>) {
    const event: Event = {
      id: generateId(),
      ...eventData,
      current_participants: 0,
      rating: 0,
      view_count: 0,
      created_at: new Date().toISOString(),
    };

    db.addToList('events', event);
    return event;
  },

  async getEvents(filters?: any) {
    let list = db.getList<Event>('events');

    if (filters?.category && filters.category !== '全部') {
      list = list.filter((e) => e.category === filters.category);
    }

    if (filters?.type && filters.type !== '全部') {
      list = list.filter((e) => e.event_type === filters.type);
    }

    if (filters?.status) {
      list = list.filter((e) => e.status === filters.status);
    }

    if (filters?.creatorId) {
      list = list.filter((e) => e.creator_id === filters.creatorId);
    }

    if (filters?.search) {
      const search = filters.search.toLowerCase();
      list = list.filter(
        (e) =>
          e.title.toLowerCase().includes(search) ||
          e.description?.toLowerCase().includes(search)
      );
    }

    // 排序
    list.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

    return list;
  },

  async getEventById(eventId: string) {
    const event = db.findInList<Event>('events', (e) => e.id === eventId)[0];
    if (event) {
      // 增加浏览次数
      db.updateInList<Event>('events', eventId, {
        view_count: event.view_count + 1,
      } as Partial<Event>);
    }
    return event;
  },

  async updateEvent(eventId: string, updates: Partial<Event>) {
    return db.updateInList<Event>('events', eventId, updates);
  },

  async deleteEvent(eventId: string) {
    return db.deleteFromList<Event>('events', eventId);
  },

  async getEventStats(creatorId: string) {
    const events = db.findInList<Event>('events', (e) => e.creator_id === creatorId);

    const stats = {
      activeCount: 0,
      completedCount: 0,
      totalRegistrations: 0,
      avgRating: 0,
    };

    events.forEach((event) => {
      if (event.status === 'active') stats.activeCount++;
      if (event.status === 'completed') stats.completedCount++;
      stats.totalRegistrations += event.current_participants;
    });

    if (events.length > 0) {
      const totalRating = events.reduce((sum, e) => sum + e.rating, 0);
      stats.avgRating = Math.round((totalRating / events.length) * 10) / 10;
    }

    return stats;
  },
};

// ==================== 报名服务 ====================
export const registrationService = {
  async createRegistration(registrationData: Omit<Registration, 'id' | 'status' | 'created_at'>) {
    await delay(500);

    // 检查是否已报名
    const existing = db.findInList<Registration>(
      'registrations',
      (r) => r.event_id === registrationData.event_id && r.phone === registrationData.phone
    );

    if (existing.length > 0) {
      throw new Error('您已报名该活动');
    }

    const registration: Registration = {
      id: generateId(),
      ...registrationData,
      status: 'confirmed',
      created_at: new Date().toISOString(),
    };

    db.addToList('registrations', registration);

    // 更新活动参与人数
    const event = db.findInList<Event>('events', (e) => e.id === registrationData.event_id)[0];
    if (event) {
      db.updateInList<Event>('events', event.id, {
        current_participants: event.current_participants + 1,
      } as Partial<Event>);
    }

    return registration;
  },

  async getRegistrations(eventId: string) {
    return db.findInList<Registration>('registrations', (r) => r.event_id === eventId);
  },

  async getUserRegistrations(userId: string, status?: string) {
    const list = db.findInList<Registration>('registrations', (r) => r.user_id === userId);
    if (status) {
      return list.filter((r) => r.status === status);
    }
    return list;
  },

  async updateRegistration(registrationId: string, updates: Partial<Registration>) {
    return db.updateInList<Registration>('registrations', registrationId, updates);
  },

  async submitReview(registrationId: string, rating: number, review: string) {
    return db.updateInList<Registration>('registrations', registrationId, {
      rating,
      review,
    } as Partial<Registration>);
  },
};

// ==================== 素材服务 ====================
export const materialService = {
  async getMaterials(filters?: any) {
    let list = db.getList<Material>('materials');

    if (filters?.type && filters.type !== '全部') {
      list = list.filter((m) => m.type === filters.type);
    }

    if (filters?.category && filters.category !== '全部') {
      list = list.filter((m) => m.category === filters.category);
    }

    if (filters?.search) {
      const search = filters.search.toLowerCase();
      list = list.filter(
        (m) =>
          m.title.toLowerCase().includes(search) ||
          m.description?.toLowerCase().includes(search)
      );
    }

    if (filters?.isFeatured) {
      list = list.filter((m) => m.is_featured);
    }

    return list;
  },

  async incrementUsage(materialId: string) {
    const material = db.findInList<Material>('materials', (m) => m.id === materialId)[0];
    if (material) {
      db.updateInList<Material>('materials', materialId, {
        usage_count: material.usage_count + 1,
      } as Partial<Material>);
    }
  },

  async toggleFavorite(userId: string, materialId: string) {
    const favorites = db.get<string[]>(`favorites_${userId}`) || [];
    const isFavorited = favorites.includes(materialId);

    if (isFavorited) {
      db.set(
        `favorites_${userId}`,
        favorites.filter((id) => id !== materialId)
      );
    } else {
      favorites.push(materialId);
      db.set(`favorites_${userId}`, favorites);
    }

    return { isFavorited: !isFavorited };
  },

  async getUserFavorites(userId: string) {
    const favoriteIds = db.get<string[]>(`favorites_${userId}`) || [];
    const materials = db.getList<Material>('materials');
    return materials.filter((m) => favoriteIds.includes(m.id));
  },
};

// ==================== 数据分析服务 ====================
export const analyticsService = {
  async getOverview(userId: string) {
    const contentData = db.findInList<ContentGeneration>(
      'content_generations',
      (g) => g.user_id === userId
    );
    const eventStats = await eventService.getEventStats(userId);

    const totalContent = contentData.length;

    return {
      totalContent,
      totalViews: Math.floor(totalContent * 29.7),
      totalInteractions: Math.floor(totalContent * 5.1),
      totalRegistrations: eventStats.totalRegistrations,
      trends: {
        content: 12.5,
        views: 23.8,
        interactions: 15.6,
        registrations: 8.3,
      },
    };
  },

  async getContentTrends(userId: string, days: number = 30) {
    // 返回模拟趋势数据
    return Array.from({ length: 7 }, (_, i) => ({
      date: `${i + 1}/1`,
      poster: Math.floor(Math.random() * 10),
      copywriting: Math.floor(Math.random() * 15),
      script: Math.floor(Math.random() * 8),
      video: Math.floor(Math.random() * 5),
    }));
  },

  async getContentDistribution(userId: string) {
    const data = db.findInList<ContentGeneration>('content_generations', (g) => g.user_id === userId);

    const distribution: Record<string, number> = {
      poster: 0,
      copywriting: 0,
      script: 0,
      video: 0,
    };

    data.forEach((item) => {
      if (item.type in distribution) {
        distribution[item.type]++;
      }
    });

    return Object.entries(distribution).map(([type, value]) => ({
      name:
        type === 'poster'
          ? '海报'
          : type === 'copywriting'
          ? '文案'
          : type === 'script'
          ? '脚本'
          : '视频',
      value,
      percentage: data.length > 0 ? Math.round((value / data.length) * 100) : 0,
    }));
  },
};

// ==================== 默认导出 ====================
export default {
  auth: authService,
  ipAnalysis: ipAnalysisService,
  content: contentService,
  event: eventService,
  registration: registrationService,
  material: materialService,
  analytics: analyticsService,
};
